import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Search, FileText, Eye, Flame, CheckCircle2, Download, Send, X, MessageCircle, Wallet, FilePlus2, TrendingUp, Plus, Users, FileSignature } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import EnviarPropostaModal from "../components/EnviarPropostaModal";
import PropostaWizard6 from "../components/PropostaWizard6";
import PropostasAbaClientes from "./PropostasAbaClientes";
import { DeleteButton } from "../components/DeleteButton";

const STATUS_OPTIONS = ["rascunho", "gerada", "enviada", "assinada", "recusada", "expirada"];
const TIPO_OPTIONS = [
  { v: "locacao", l: "Locação" },
  { v: "venda", l: "Venda" },
  { v: "servico", l: "Serviço" },
  { v: "outros", l: "Outros" },
];

// Cores conforme spec: 🔵 cadastrado (rascunho) / 🟡 proposta feita (gerada) / 🟢 enviada / 💚 assinada
const STATUS_COLOR = {
  rascunho: "bg-blue-100 text-blue-700",
  gerada: "bg-amber-100 text-amber-800",
  enviada: "bg-emerald-100 text-emerald-800",
  assinada: "bg-emerald-200 text-emerald-900",
  recusada: "bg-red-100 text-red-700",
  expirada: "bg-gray-100 text-gray-500",
};
const STATUS_LABEL = {
  rascunho: "Cadastrado",
  gerada: "Proposta feita",
  enviada: "Enviada",
  assinada: "Assinada",
  recusada: "Recusada",
  expirada: "Expirada",
};

const PERIODOS = [
  { v: "todos", l: "Todos os períodos", horas: null },
  { v: "24h", l: "Últimas 24h", horas: 24 },
  { v: "7d", l: "7 dias", horas: 24 * 7 },
  { v: "30d", l: "30 dias", horas: 24 * 30 },
  { v: "90d", l: "90 dias", horas: 24 * 90 },
  { v: "custom", l: "Personalizado", horas: null },
];

const fmtBRL = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

export default function Propostas() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tipo, setTipo] = useState("");
  const [periodo, setPeriodo] = useState("todos");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  const [vendedorFiltro, setVendedorFiltro] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [propEnviar, setPropEnviar] = useState(null);
  const [novaPropOpen, setNovaPropOpen] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const isDirOrSup = ["diretor", "supervisao", "gerente"].includes(user?.role);
  const canCreate = ["vendedor", "diretor", "supervisao", "gerente"].includes(user?.role) ||
    (user?.role === "operador" && user?.escopo === "geral");

  // Carregar vendedores para filtro (diretor/supervisao/gerente)
  useEffect(() => {
    if (!isDirOrSup) return;
    api.get("/comercial/vendedores")
      .then((r) => setVendedores(r.data || []))
      .catch(() => {});
  }, [isDirOrSup]);

  // Calcular intervalo de data com base no periodo selecionado
  const computeDesdeAte = useCallback(() => {
    if (periodo === "custom") return { desde, ate };
    const p = PERIODOS.find((x) => x.v === periodo);
    if (!p || !p.horas) return { desde: "", ate: "" };
    const agora = new Date();
    const inicio = new Date(agora.getTime() - p.horas * 60 * 60 * 1000);
    return { desde: inicio.toISOString(), ate: "" };
  }, [periodo, desde, ate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const myReq = ++reqIdRef.current;
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (tipo) params.tipo_contrato = tipo;
      const { desde: d, ate: a } = computeDesdeAte();
      if (d) params.desde = d;
      if (a) params.ate = a;
      if (vendedorFiltro) params.vendedor_id = vendedorFiltro;
      // DIAG /app/propostas — log temporário para inspeção em produção
      console.log("[/app/propostas] enviando params:", params, "user:", { role: user?.role, escopo: user?.escopo, unidade_id: user?.unidade_id });
      const { data } = await api.get("/proposta-nativa/search/buscar", { params });
      if (myReq !== reqIdRef.current) return;
      console.log("[/app/propostas] retorno:", { total: data.total, len: data.items?.length });
      setItems(data.items || []);
    } catch (e) {
      console.error("[/app/propostas] erro:", e?.response?.status, e?.response?.data);
      toast.error("Falha na busca");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [q, status, tipo, computeDesdeAte, vendedorFiltro, user]);

  // Debounce 300ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchData, 300);
    return () => clearTimeout(debounceRef.current);
  }, [fetchData]);

  const limparFiltros = () => {
    setQ("");
    setStatus("");
    setTipo("");
    setPeriodo("todos");
    setDesde("");
    setAte("");
    setVendedorFiltro("");
  };

  const baixarPdf = async (p) => {
    try {
      const { data } = await api.get(`/proposta-nativa/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposta_${p.numero || p.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao baixar PDF");
    }
  };

  const enviar = (p, e) => {
    e?.stopPropagation?.();
    setPropEnviar(p);
  };

  const enviarWhatsapp = async (p, e) => {
    e.stopPropagation();
    if (!p.cliente_telefone) {
      toast.error("Cliente sem telefone cadastrado. Edite a proposta para incluir.");
      return;
    }
    if (!window.confirm(`Enviar proposta ${p.numero} via WhatsApp para ${p.cliente_telefone}?`)) return;
    try {
      const { data } = await api.post(`/proposta-nativa/${p.id}/enviar-whatsapp`);
      toast.success(`WhatsApp enviado (${data.whatsapp?.mode || "ok"})`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar WhatsApp");
    }
  };

  const totalEnviadas = useMemo(() => items.filter((p) => p.status === "enviada").length, [items]);
  const totalAssinadas = useMemo(() => items.filter((p) => p.status === "assinada").length, [items]);
  const totalAltaTemp = useMemo(() => items.filter((p) => (p.opens_count || 0) >= 2 && p.status === "enviada").length, [items]);
  const totalElaboracao = useMemo(() => items.filter((p) => p.status === "rascunho" || p.status === "gerada").length, [items]);
  const valorTotalCarteira = useMemo(() => items.reduce((s, p) => s + (Number(p.valor_total_geral) || 0), 0), [items]);

  return (
    <div className="space-y-5" data-testid="propostas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <FileText size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Propostas
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? "Buscando..." : `${items.length} resultado${items.length === 1 ? "" : "s"}`} no período selecionado
              {totalAltaTemp > 0 && ` · 🔥 ${totalAltaTemp} em alta temperatura`}
            </p>
          </div>
        </div>
        {canCreate && (
          <Button
            onClick={() => setNovaPropOpen(true)}
            className="text-white"
            style={{ background: "#F97316" }}
            data-testid="propostas-nova-btn"
          >
            <Plus size={14} className="mr-1" /> Nova proposta
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      <Tabs defaultValue="propostas" className="space-y-4">
        <TabsList data-testid="propostas-tabs">
          <TabsTrigger value="propostas" data-testid="tab-propostas-lista">
            <FileText size={14} className="mr-1" /> Propostas
          </TabsTrigger>
          <TabsTrigger value="clientes" data-testid="tab-propostas-clientes">
            <Users size={14} className="mr-1" /> Clientes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="propostas" className="space-y-4 m-0">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="propostas-kpis">
        <KpiCard icon={FileText} label="Total no período" value={items.length} cor="#1B3A6B" />
        <KpiCard icon={FilePlus2} label="Em elaboração" value={totalElaboracao} cor="#3b82f6" hint="Cadastrado + Proposta feita" />
        <KpiCard icon={Send} label="Enviadas" value={totalEnviadas} cor="#16a34a" hint={totalAssinadas > 0 ? `${totalAssinadas} já assinada(s)` : null} />
        <KpiCard icon={Wallet} label="Valor total" value={fmtBRL(valorTotalCarteira)} cor="#F97316" hint="Soma dos equipamentos" />
      </div>

      {/* Pills de período */}
      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="propostas-pills">
          <span className="text-xs uppercase font-semibold text-muted-foreground mr-1">Período:</span>
          {PERIODOS.map((p) => (
            <button
              key={p.v}
              data-testid={`pill-${p.v}`}
              onClick={() => setPeriodo(p.v)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${periodo === p.v ? "text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              style={periodo === p.v ? { background: "#1B3A6B" } : {}}
            >
              {p.l}
            </button>
          ))}
          {periodo === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Input data-testid="propostas-filtro-desde" type="date" className="h-8" value={desde} onChange={(e) => setDesde(e.target.value)} title="Desde" />
              <Input data-testid="propostas-filtro-ate" type="date" className="h-8" value={ate} onChange={(e) => setAte(e.target.value)} title="Até" />
            </div>
          )}
        </div>
      </Card>

      {/* Busca + filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="propostas-search"
              className="pl-9"
              placeholder="Buscar por cliente, CNPJ/CPF, e-mail, número..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            data-testid="propostas-filtro-status"
            className="md:col-span-3 border rounded-md px-3 py-2 text-sm bg-background"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Todos os status</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
          <select
            data-testid="propostas-filtro-tipo"
            className="md:col-span-2 border rounded-md px-3 py-2 text-sm bg-background"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Todos tipos</option>
            {TIPO_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          {isDirOrSup && (
            <select
              data-testid="propostas-filtro-vendedor"
              className="md:col-span-2 border rounded-md px-3 py-2 text-sm bg-background"
              value={vendedorFiltro}
              onChange={(e) => setVendedorFiltro(e.target.value)}
            >
              <option value="">Todos vendedores</option>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.nome || v.email}</option>)}
            </select>
          )}
        </div>
        {(q || status || tipo || vendedorFiltro || (periodo === "custom" && (desde || ate))) && (
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="text-muted-foreground">Filtros ativos:</span>
            {q && <Badge variant="outline">"{q}"</Badge>}
            {status && <Badge variant="outline">status: {STATUS_LABEL[status] || status}</Badge>}
            {tipo && <Badge variant="outline">tipo: {tipo}</Badge>}
            {vendedorFiltro && <Badge variant="outline">vendedor: {vendedores.find((v) => v.id === vendedorFiltro)?.nome || vendedorFiltro.slice(0, 8)}</Badge>}
            {desde && <Badge variant="outline">desde {desde}</Badge>}
            {ate && <Badge variant="outline">até {ate}</Badge>}
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={limparFiltros} data-testid="propostas-limpar">
              <X size={12} className="mr-1" /> Limpar
            </Button>
          </div>
        )}
      </Card>

      {/* Resultados */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Proposta</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tracking</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Data</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground" data-testid="propostas-empty">
                  {loading ? (
                    "Carregando..."
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <FileText size={32} className="text-muted-foreground/50" />
                      <div className="font-medium">Nenhuma proposta encontrada</div>
                      <div className="text-xs max-w-md">
                        {(q || status || tipo || vendedorFiltro || periodo !== "todos") ? (
                          <>Tente <button onClick={limparFiltros} className="underline text-blue-700">limpar os filtros</button> ou amplie o período para "30 dias".</>
                        ) : (
                          <>Você ainda não tem propostas neste período. Clique no botão abaixo para criar a primeira.</>
                        )}
                      </div>
                      <Button
                        onClick={() => setNovaPropOpen(true)}
                        size="sm"
                        className="text-white mt-1"
                        style={{ background: "#F97316" }}
                        data-testid="propostas-nova-btn-empty"
                      >
                        <Plus size={14} className="mr-1" /> Nova proposta
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : items.map((p) => {
              const opens = p.opens_count || 0;
              const clicks = p.clicks_count || 0;
              const alta = opens >= 2 && p.status === "enviada";
              return (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-slate-50"
                  data-testid={`row-${p.id}`}
                  onClick={() => navigate(`/app/leads/${p.lead_id}`)}
                >
                  <TableCell>
                    <div className="font-mono text-sm font-bold" style={{ color: "#1B3A6B" }}>{p.numero}</div>
                    <div className="text-xs text-muted-foreground">por {p.vendedor_nome}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{p.cliente_nome}</div>
                    <div className="text-xs text-muted-foreground">{p.cliente_email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {TIPO_OPTIONS.find((t) => t.v === p.tipo_contrato)?.l || p.tipo_contrato}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLOR[p.status] || "bg-slate-100"} variant="secondary" data-testid={`status-${p.id}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </Badge>
                    {p.status === "assinada" && (
                      <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1">
                        <CheckCircle2 size={10} /> {p.assinante_nome}
                      </div>
                    )}
                    {(p.versao || 1) > 1 && (
                      <Badge variant="outline" className="text-[10px] mt-0.5">v{p.versao}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.status === "enviada" ? (
                      <div className="flex flex-col gap-0.5 text-xs">
                        {opens > 0 && <span className="flex items-center gap-1 text-blue-700"><Eye size={11} /> {opens}× aberto</span>}
                        {clicks > 0 && <span className="flex items-center gap-1 text-emerald-700">🔗 {clicks}× clicado</span>}
                        {opens === 0 && clicks === 0 && <span className="text-muted-foreground">Sem aberturas</span>}
                        {alta && (
                          <Badge className="text-white text-xs mt-0.5 w-fit" style={{ background: "linear-gradient(90deg, #F97316, #DC2626)" }}>
                            <Flame size={10} className="mr-1" /> Alta temperatura
                          </Badge>
                        )}
                      </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="font-mono font-bold" style={{ color: "#1B3A6B" }}>{fmtBRL(p.valor_total_geral)}</div>
                    {p.tipo_contrato === "locacao" && (
                      <div className="text-xs text-muted-foreground">{fmtBRL(p.valor_mensal_total)}/mês</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm">{fmtData(p.criado_em)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                      {p.status !== "assinada" && p.status !== "expirada" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => enviar(p, e)}
                          data-testid={`enviar-${p.id}`}
                          title={p.status === "enviada" ? "Reenviar com CC/mensagem editada" : "Enviar ao cliente"}
                        >
                          <Send size={14} />
                          {p.status === "enviada" && <span className="ml-1 text-xs">Reenviar</span>}
                        </Button>
                      )}
                      {(p.status === "gerada" || p.status === "rascunho" || p.status === "enviada") && p.cliente_telefone && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => enviarWhatsapp(p, e)}
                          data-testid={`whatsapp-${p.id}`}
                          title="Enviar via WhatsApp"
                          className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700"
                        >
                          <MessageCircle size={14} />
                        </Button>
                      )}
                      {["enviada", "assinada", "aprovada"].includes(p.status) && p.lead_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/app/leads/${p.lead_id}?tab=assinaturas`); }}
                          data-testid={`assinaturas-${p.id}`}
                          title="Assinaturas"
                        >
                          <FileSignature size={14} />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); baixarPdf(p); }}
                        data-testid={`baixar-${p.id}`}
                        title="Baixar PDF"
                      >
                        <Download size={14} />
                      </Button>
                      <DeleteButton
                        url={`/propostas/${p.id}`}
                        label={`Proposta ${p.numero || p.id.slice(0, 8)}`}
                        onDeleted={fetchData}
                        testId={`del-proposta-${p.id}`}
                        iconOnly
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
        </TabsContent>

        <TabsContent value="clientes" className="m-0">
          <PropostasAbaClientes />
        </TabsContent>
      </Tabs>

      {/* Modal de envio com CC + edição de corpo + preview */}
      <EnviarPropostaModal
        proposta={propEnviar}
        open={!!propEnviar}
        onOpenChange={(v) => { if (!v) setPropEnviar(null); }}
        onSent={() => { setPropEnviar(null); fetchData(); }}
      />

      {/* Wizard de 6 etapas — Cliente · Modelo Proposta · Modelo Contrato · Equipamentos · Condição · Assinaturas */}
      <PropostaWizard6
        open={novaPropOpen}
        onOpenChange={setNovaPropOpen}
        onCreated={() => { setNovaPropOpen(false); fetchData(); }}
      />
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, cor, hint }) => (
  <Card className="p-4 border-l-4" style={{ borderLeftColor: cor }}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
      <Icon size={12} /> {label}
    </div>
    <div className="text-2xl font-display font-bold mt-1.5" style={{ color: "#1B3A6B" }}>{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
  </Card>
);
